/**
 * Central access checker. The single source of truth for "what does this
 * user actually have access to right now?".
 *
 * All UI gating and admin tools should consume this helper instead of
 * reading raw profile fields, so that we never re-introduce the bug where
 * one product flag (`ai_plan = 'plus'`) was treated as permanent paid
 * access regardless of the actual Lemon Squeezy subscription state.
 */

export type AiBillingType = 'monthly' | 'yearly' | null;
export type ProBillingType = 'monthly' | 'yearly' | null;
export type AccessSource = 'lemonsqueezy' | 'manual' | 'included_in_pro' | null;

/** Subset of the `profiles` row we need to reason about access. */
export interface AccessProfileRow {
  // AI Assistant Plus
  ai_plan?: string | null;
  ai_subscription_status?: string | null;
  ai_subscription_type?: string | null;
  ai_period_start?: string | null;
  ai_period_end?: string | null;
  ai_daily_limit?: number | null;
  ai_access_source?: string | null;
  ai_lemonsqueezy_subscription_id?: string | null;

  // Pro
  pro_subscription_status?: string | null;
  pro_subscription_type?: string | null;
  pro_period_start?: string | null;
  pro_period_end?: string | null;
  pro_access_source?: string | null;
  pro_lemonsqueezy_subscription_id?: string | null;

  // Document Pack
  has_document_pack?: boolean | null;
  has_word_pack?: boolean | null; // legacy flag - mirror of has_document_pack
  document_pack_order_id?: string | null;
  document_pack_purchase_date?: string | null;
  document_pack_access_source?: string | null;

  // Manual admin grants
  manual_ai_plus?: boolean | null;
  manual_pro_access?: boolean | null;
  manual_doc_pack?: boolean | null;
  manual_access_note?: string | null;
  manual_access_expires_at?: string | null;

  // Legacy mirror fields - read for backwards compatibility only
  subscription_plan?: string | null;
  subscription_start_date?: string | null;
  subscription_end_date?: string | null;
}

export interface UserAccess {
  ai: {
    active: boolean;
    status: string | null;
    billing_type: AiBillingType;
    period_start: string | null;
    period_end: string | null;
    daily_limit: number;
    access_source: AccessSource;
  };
  pro: {
    active: boolean;
    status: string | null;
    billing_type: ProBillingType;
    period_start: string | null;
    period_end: string | null;
    access_source: AccessSource;
  };
  doc_pack: {
    active: boolean;
    purchase_date: string | null;
    access_source: AccessSource;
  };
  manual: {
    ai_plus: boolean;
    pro_access: boolean;
    doc_pack: boolean;
    note: string | null;
    expires_at: string | null;
    expired: boolean;
  };
  warnings: string[];
}

const SUSPICIOUS_FUTURE_YEARS = 2; // dates more than 2 years out are suspicious

function isPast(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.getTime() < Date.now();
}

function isFuture(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  return !isNaN(d.getTime()) && d.getTime() > Date.now();
}

function tooFarInFuture(iso: string | null | undefined): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  if (isNaN(d.getTime())) return false;
  const limit = new Date();
  limit.setFullYear(limit.getFullYear() + SUSPICIOUS_FUTURE_YEARS);
  return d.getTime() > limit.getTime();
}

function normalizeBilling(v: string | null | undefined): 'monthly' | 'yearly' | null {
  if (!v) return null;
  const s = String(v).toLowerCase();
  if (s.includes('year') || s.includes('annual')) return 'yearly';
  if (s.includes('month')) return 'monthly';
  return null;
}

function normalizeSource(v: string | null | undefined): AccessSource {
  if (!v) return null;
  const s = String(v).toLowerCase();
  if (s === 'lemonsqueezy' || s === 'manual' || s === 'included_in_pro') return s;
  return null;
}

export function getUserAccess(profile: AccessProfileRow | null | undefined): UserAccess {
  const warnings: string[] = [];
  const p = profile ?? {};

  // ---------- Manual block (computed first so other rules can defer to it) ----------
  const manualExpired =
    !!p.manual_access_expires_at && isPast(p.manual_access_expires_at);
  const manualAi = !!p.manual_ai_plus && !manualExpired;
  const manualPro = !!p.manual_pro_access && !manualExpired;
  const manualDoc = !!p.manual_doc_pack && !manualExpired;

  // ---------- Pro ----------
  const proLsActive =
    p.pro_subscription_status === 'active' ||
    p.pro_subscription_status === 'on_trial' ||
    p.pro_subscription_status === 'past_due';
  const proPeriodStillValid = !p.pro_period_end || isFuture(p.pro_period_end);
  // Legacy fallback - older records only have `subscription_plan`/`subscription_end_date`.
  const legacyProActive =
    !!p.subscription_plan && isFuture(p.subscription_end_date);

  const proActive = (proLsActive && proPeriodStillValid) || manualPro || legacyProActive;

  const proSource: AccessSource = manualPro
    ? 'manual'
    : proLsActive || legacyProActive
      ? normalizeSource(p.pro_access_source) ?? 'lemonsqueezy'
      : null;

  // ---------- AI Assistant Plus ----------
  const aiLsActive =
    p.ai_subscription_status === 'active' ||
    p.ai_subscription_status === 'on_trial' ||
    p.ai_subscription_status === 'past_due';
  const aiPeriodStillValid = !p.ai_period_end || isFuture(p.ai_period_end);

  // AI is active if a real LS AI sub exists, OR Pro is active (Pro includes AI Plus),
  // OR manual AI Plus is granted.
  const aiActive =
    (aiLsActive && aiPeriodStillValid) || proActive || manualAi;

  const aiSource: AccessSource = aiLsActive && aiPeriodStillValid
    ? normalizeSource(p.ai_access_source) ?? 'lemonsqueezy'
    : proActive && !manualAi
      ? 'included_in_pro'
      : manualAi
        ? 'manual'
        : null;

  // Daily limit:
  //   - explicit ai_daily_limit if set
  //   - 150 if user has Pro (Pro includes higher allowance)
  //   - 30 if standalone AI Plus is active
  //   - 5 otherwise (free tier)
  const aiDailyLimit = aiActive
    ? typeof p.ai_daily_limit === 'number' && p.ai_daily_limit > 0
      ? p.ai_daily_limit
      : proActive
        ? 150
        : 30
    : 5;

  // ---------- Document Pack ----------
  // One-time purchase: never auto-revoke. Once true, stays true.
  const docFromLs =
    !!p.has_document_pack || !!p.has_word_pack || !!p.document_pack_order_id;
  const docActive = docFromLs || manualDoc;

  const docSource: AccessSource = manualDoc
    ? 'manual'
    : docFromLs
      ? normalizeSource(p.document_pack_access_source) ?? 'lemonsqueezy'
      : null;

  // ---------- Warnings ----------
  if (aiActive && !p.ai_period_end && !proActive && !manualAi) {
    warnings.push('AI Plus active but missing subscription period.');
  }
  if (proActive && !p.pro_period_end && !manualPro && !legacyProActive) {
    warnings.push('Pro active but missing subscription period.');
  }
  if (tooFarInFuture(p.ai_period_end)) {
    warnings.push('AI Plus period_end is more than 2 years in the future.');
  }
  if (tooFarInFuture(p.pro_period_end)) {
    warnings.push('Pro period_end is more than 2 years in the future.');
  }
  if (tooFarInFuture(p.subscription_end_date)) {
    warnings.push('Legacy subscription_end_date is more than 2 years in the future.');
  }
  if (tooFarInFuture(p.manual_access_expires_at)) {
    warnings.push('Manual access expiry is more than 2 years in the future.');
  }
  if (
    p.ai_plan === 'plus' &&
    !aiLsActive &&
    !proActive &&
    !manualAi
  ) {
    warnings.push("ai_plan = 'plus' but no active LS subscription, no active Pro, and no manual override.");
  }
  if (p.ai_subscription_status === 'expired' || p.ai_subscription_status === 'cancelled') {
    if (aiLsActive) {
      // sanity check - shouldn't happen, but flag if status text is contradictory
      warnings.push('Contradictory AI status flags.');
    }
  }

  return {
    ai: {
      active: aiActive,
      status: p.ai_subscription_status ?? null,
      billing_type: normalizeBilling(p.ai_subscription_type),
      period_start: p.ai_period_start ?? null,
      period_end: p.ai_period_end ?? null,
      daily_limit: aiDailyLimit,
      access_source: aiSource,
    },
    pro: {
      active: proActive,
      status: p.pro_subscription_status ?? (legacyProActive ? 'active' : null),
      billing_type:
        normalizeBilling(p.pro_subscription_type) ??
        normalizeBilling(p.subscription_plan),
      period_start: p.pro_period_start ?? null,
      period_end: p.pro_period_end ?? p.subscription_end_date ?? null,
      access_source: proSource,
    },
    doc_pack: {
      active: docActive,
      purchase_date: p.document_pack_purchase_date ?? null,
      access_source: docSource,
    },
    manual: {
      ai_plus: !!p.manual_ai_plus,
      pro_access: !!p.manual_pro_access,
      doc_pack: !!p.manual_doc_pack,
      note: p.manual_access_note ?? null,
      expires_at: p.manual_access_expires_at ?? null,
      expired: manualExpired,
    },
    warnings,
  };
}

/** Convenience: collapse to legacy shape used elsewhere in the app. */
export function legacyTierFromAccess(
  access: UserAccess
): { plan: 'free' | 'pro'; aiPlan: 'free' | 'pro_tools' | 'plus'; hasWordPack: boolean } {
  const plan = access.pro.active ? 'pro' : 'free';
  // Plus when AI is active for any reason; pro_tools fallback for legacy Pro
  // subscribers without an explicit AI grant; otherwise free.
  let aiPlan: 'free' | 'pro_tools' | 'plus' = 'free';
  if (access.ai.active) aiPlan = 'plus';
  else if (access.pro.active) aiPlan = 'pro_tools';
  return { plan, aiPlan, hasWordPack: access.doc_pack.active };
}